<script setup lang="ts">
import { brandRef } from "../brand-state";
/**
 * A source's data access agreement as a thing you can finish.
 *
 * "Draft — not signed" used to be a dead end: the status was right, but
 * nothing said who had to sign, what was still undecided, or how a
 * signature got recorded (edit the markdown in the repository and run a
 * deploy script). This card answers those in order — what is open, who
 * signs, and a button for each signature — and signing happens here.
 *
 * A signature is recorded by an admin in the signer's name, with evidence:
 * the signed PDF, or a note saying where the signed copy is kept. It counts
 * for the version of the text it was given for; a new version reads
 * unsigned until it is signed again. A signature recorded by mistake is
 * withdrawn with a reason, never deleted.
 */
import { vWrite } from "../admin-read-only";
import { computed, ref } from "vue";
import {
  AdminError,
  type AgreementSigner,
  type DataSource,
  getDataSourceAgreement,
  getSignedCopy,
  recordAgreementSignature,
  revokeAgreementSignature,
} from "../api/admin";
import { useConfirm } from "../composables/useConfirm";
import { useToast } from "../composables/useToast";
import { fillPrintWindow, openPrintWindow } from "../utils/agreement";
import { saveBlob } from "../utils/download";
import StatusChip from "./StatusChip.vue";

const props = defineProps<{ source: DataSource; devUser: string }>();
const emit = defineEmits<{
  (e: "updated", s: DataSource): void;
  (e: "read"): void;
}>();

const toast = useToast();
const { confirm } = useConfirm();

const meta = computed(() => props.source.agreement_meta);
const state = computed(() => props.source.agreement_state);

const blocking = computed(
  () => meta.value?.decisions.filter((d) => d.kind === "needs_data") ?? [],
);
const pending = computed(
  () => meta.value?.decisions.filter((d) => d.kind === "needs_review") ?? [],
);

const headline = computed(() => {
  const m = meta.value;
  if (!m) return "";
  if (state.value === "signed") return "Signed by everyone — in force";
  if (blocking.value.length)
    return `Can't be signed yet — ${blocking.value.length} missing ${blocking.value.length === 1 ? "value" : "values"}`;
  const left = m.signatures.needed - m.signatures.dated;
  return `Waiting for ${left} of ${m.signatures.needed} ${m.signatures.needed === 1 ? "signature" : "signatures"}`;
});

// ── Long decisions fold to one line until asked ───────────────────────────

const LONG = 180;
const expandedDecisions = ref<Set<number>>(new Set());
function decisionText(i: number, text: string): string {
  if (text.length <= LONG || expandedDecisions.value.has(i)) return text;
  return `${text.slice(0, LONG).trimEnd()}…`;
}
function toggleDecision(i: number): void {
  const next = new Set(expandedDecisions.value);
  if (next.has(i)) next.delete(i);
  else next.add(i);
  expandedDecisions.value = next;
}

// ── Who signs ─────────────────────────────────────────────────────────────

/** The person named for this role: the agreement's own table first, then
 *  Compliance → Responsibilities (which may be a placeholder). */
function holder(s: AgreementSigner, none = "Not named"): string {
  if (s.name) return s.name;
  const names = (s.people ?? []).map((p) => p.name).filter(Boolean);
  return names.length ? names.join(", ") : none;
}
function email(s: AgreementSigner): string {
  return (s.people ?? []).find((p) => p.email)?.email ?? "";
}
function isPlaceholder(s: AgreementSigner): boolean {
  return !email(s);
}

/** A ready-to-send request: what to sign, and where. They sign in DOIT AI
 *  under My sign-offs — no attachment, no printing. */
function askLink(s: AgreementSigner): string {
  const m = meta.value;
  const url = `${window.location.origin}${window.location.pathname}#/sign-offs`;
  const subject = `Please sign ${m?.id ?? "a data access agreement"} — ${props.source.label}`;
  const body =
    `Hi ${holder(s, "")},\n\n` +
    `${brandRef.value.productName} needs your signature on ${m?.id} version ${m?.version}, the data access agreement for ${props.source.label}, ` +
    `as ${s.role}.\n\n` +
    `You can read and sign it here (sign in with your work account):\n${url}\n\n` +
    `If anything in it is wrong, reply to this mail instead of signing.\n`;
  return `mailto:${encodeURIComponent(email(s))}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

// ── Print for signature ───────────────────────────────────────────────────

async function print(): Promise<void> {
  // Opened in the click, filled after the fetch: a window opened after an
  // await is a popup, and the browser blocks it.
  const w = openPrintWindow();
  if (!w) {
    toast.error(
      "Could not open the print window",
      "Allow pop-ups for this site.",
    );
    return;
  }
  try {
    const a = await getDataSourceAgreement(props.source.key, props.devUser);
    fillPrintWindow(
      w,
      `${a.meta.id || "Agreement"} v${a.meta.version} — ${a.label}`,
      a.markdown,
    );
  } catch (e) {
    w.close();
    toast.error(
      "Could not load the agreement",
      e instanceof AdminError ? e.message : String(e),
    );
  }
}

// ── Record a signature ────────────────────────────────────────────────────

const today = new Date().toISOString().slice(0, 10);
const formRole = ref<string | null>(null);
const formName = ref("");
const formDate = ref(today);
const formNote = ref("");
const formFile = ref<File | null>(null);
const saving = ref(false);

function startRecord(s: AgreementSigner): void {
  formRole.value = s.role;
  formName.value = s.name || holder(s, "");
  formDate.value = today;
  formNote.value = "";
  formFile.value = null;
}

function onFile(ev: Event): void {
  const input = ev.target as HTMLInputElement;
  formFile.value = input.files?.[0] ?? null;
}

const canSave = computed(
  () =>
    !!formName.value.trim() &&
    !!formDate.value &&
    (!!formFile.value || !!formNote.value.trim()),
);

async function saveSignature(): Promise<void> {
  if (!formRole.value || !canSave.value || saving.value) return;
  saving.value = true;
  try {
    const updated = await recordAgreementSignature(
      props.source.key,
      {
        role: formRole.value,
        signer_name: formName.value.trim(),
        signed_on: formDate.value,
        note: formNote.value.trim(),
        file: formFile.value,
      },
      props.devUser,
    );
    toast.success(`Signature recorded — ${formRole.value}`);
    formRole.value = null;
    emit("updated", updated);
  } catch (e) {
    toast.error(
      "Could not record the signature",
      e instanceof AdminError ? e.message : String(e),
    );
  } finally {
    saving.value = false;
  }
}

async function withdraw(s: AgreementSigner): Promise<void> {
  const rec = s.recorded;
  if (!rec) return;
  const reason = window.prompt(
    `Withdraw ${rec.signer_name}'s signature for ${s.role}? It stays in the history. Why?`,
  );
  if (!reason?.trim()) return;
  try {
    const updated = await revokeAgreementSignature(
      props.source.key,
      rec.id,
      reason.trim(),
      props.devUser,
    );
    toast.success("Signature withdrawn");
    emit("updated", updated);
  } catch (e) {
    toast.error(
      "Could not withdraw the signature",
      e instanceof AdminError ? e.message : String(e),
    );
  }
}

async function downloadCopy(s: AgreementSigner): Promise<void> {
  const rec = s.recorded;
  if (!rec) return;
  try {
    const blob = await getSignedCopy(props.source.key, rec.id, props.devUser);
    saveBlob(blob, rec.document_name || `${rec.agreement_id}-signed.pdf`);
  } catch (e) {
    toast.error(
      "Could not download the signed copy",
      e instanceof AdminError ? e.message : String(e),
    );
  }
}

// Signing while decisions are open is allowed — the signer may accept them
// as written — but it should be a choice, not an accident.
async function onRecordClick(s: AgreementSigner): Promise<void> {
  if (pending.value.length && formRole.value === null) {
    const ok = await confirm({
      title: "Decisions are still open",
      message: `${pending.value.length === 1 ? "One decision belongs" : `${pending.value.length} decisions belong`} to someone else. Record this signature anyway? The signer then accepts the text as it stands.`,
      confirmLabel: "Record anyway",
    });
    if (!ok) return;
  }
  startRecord(s);
}
</script>

<template>
  <section class="ag">
    <p v-if="state === 'exempt'" class="small">
      <StatusChip tone="muted">None needed</StatusChip>
      {{ source.agreement_exempt }}
    </p>
    <p v-else-if="!meta" class="small gap-text">
      The agreement for this source could not be found. Ask whoever deploys this
      platform to check that it was published with the configuration.
    </p>

    <template v-else>
      <div class="ag__head">
        <div>
          <div class="ag__title">
            {{ meta.id }}
            <span class="muted">· version {{ meta.version }}</span>
          </div>
          <div
            :class="[
              'ag__headline',
              state === 'signed'
                ? 'ok-text'
                : blocking.length
                  ? 'gap-text'
                  : '',
            ]"
          >
            {{ headline }}
          </div>
        </div>
        <div class="ag__actions">
          <button class="btn" @click="emit('read')">Read</button>
          <button v-if="state !== 'signed'" class="btn" @click="print">
            Print for signature
          </button>
        </div>
      </div>

      <!-- 1. What stops it -->
      <div v-if="blocking.length || pending.length" class="ag__block">
        <h5>
          {{
            blocking.length
              ? "Missing before anyone can sign"
              : "Open decisions"
          }}
        </h5>
        <p v-if="!blocking.length" class="muted small">
          These belong to someone other than the data owner. Settle them first,
          or let the signers accept the text as it stands.
        </p>
        <ul class="ag__decisions">
          <li
            v-for="(d, i) in [...blocking, ...pending]"
            :key="i"
            :class="d.kind === 'needs_data' ? 'gap-text' : ''"
          >
            {{ decisionText(i, d.text) }}
            <button
              v-if="d.text.length > LONG"
              class="link small"
              @click="toggleDecision(i)"
            >
              {{ expandedDecisions.has(i) ? "less" : "more" }}
            </button>
          </li>
        </ul>
      </div>

      <!-- 2. Who signs -->
      <div class="ag__block">
        <h5>
          Signatures · {{ meta.signatures.dated }} of
          {{ meta.signatures.needed }}
        </h5>
        <ul class="ag__signers">
          <li v-for="s in meta.signers" :key="s.role" class="signer">
            <span
              :class="['signer__dot', s.signed_on && 'signer__dot--done']"
              aria-hidden="true"
            />
            <div class="signer__body">
              <div class="signer__role">{{ s.role }}</div>
              <div class="small">
                <template v-if="s.signed_on">
                  Signed by <strong>{{ s.name || "—" }}</strong> on
                  {{ s.signed_on }}
                  <template v-if="s.recorded">
                    <span
                      v-if="s.recorded.method === 'self'"
                      class="muted"
                      title="Signed with their own sign-in; the exact text they accepted is recorded"
                    >
                      · signed in {{ brandRef.productName }}</span
                    >
                    <span v-else class="muted">
                      · recorded by {{ s.recorded.recorded_by_email }}</span
                    >
                    <template v-if="s.recorded.has_document">
                      ·
                      <button v-write class="link" @click="downloadCopy(s)">
                        signed copy
                      </button></template
                    >
                    <template v-else-if="s.recorded.note">
                      <span class="muted"> · {{ s.recorded.note }}</span>
                    </template>
                    ·
                    <button v-write class="link muted" @click="withdraw(s)">
                      withdraw
                    </button>
                  </template>
                  <span v-else class="muted"> · in the document</span>
                </template>
                <template v-else>
                  <span class="muted">{{ holder(s) }} — not signed</span>
                  <span
                    v-if="isPlaceholder(s)"
                    class="placeholder"
                    title="No e-mail yet: they cannot sign in here, so an admin records their signature. Name them under Approvals → Who signs."
                    >placeholder</span
                  >
                  <template v-else-if="email(s)">
                    ·
                    <a class="link" :href="askLink(s)">ask by e-mail</a>
                  </template>
                  <span v-if="s.previous" class="warn-text">
                    · signed version {{ s.previous.agreement_version }}, needs
                    to sign this one</span
                  >
                </template>
              </div>

              <!-- The form opens under the signer it is for. -->
              <form
                v-if="formRole === s.role"
                class="sign-form"
                v-write
                @submit.prevent="saveSignature"
              >
                <label>
                  Signed by
                  <input
                    v-model="formName"
                    maxlength="200"
                    placeholder="Full name"
                    required
                  />
                </label>
                <label>
                  On
                  <input v-model="formDate" type="date" :max="today" required />
                </label>
                <label class="sign-form__wide">
                  Signed copy (PDF)
                  <input
                    type="file"
                    accept="application/pdf,.pdf"
                    @change="onFile"
                  />
                </label>
                <label class="sign-form__wide">
                  {{
                    formFile
                      ? "Note (optional)"
                      : "Or: where the signed copy is"
                  }}
                  <input
                    v-model="formNote"
                    maxlength="500"
                    placeholder="e.g. DocuSign envelope, Risma record"
                  />
                </label>
                <div class="sign-form__actions">
                  <button
                    type="button"
                    class="btn btn--ghost"
                    @click="formRole = null"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    class="btn btn--primary"
                    :disabled="!canSave || saving"
                  >
                    {{ saving ? "Recording…" : "Record signature" }}
                  </button>
                </div>
              </form>
            </div>
            <button
              v-if="!s.signed_on && formRole !== s.role"
              class="btn"
              :disabled="blocking.length > 0"
              :title="
                blocking.length
                  ? 'Fill in the missing values in the agreement first'
                  : ''
              "
              v-write
              @click="onRecordClick(s)"
            >
              Record signature
            </button>
          </li>
        </ul>
        <p v-if="meta.open.assumed" class="muted small">
          Signing also accepts {{ meta.open.assumed }}
          {{ meta.open.assumed === 1 ? "assumption" : "assumptions" }} marked
          amber in the text.
        </p>
      </div>
    </template>
  </section>
</template>

<style scoped>
.ag {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 0.75rem 0.9rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
}
.ag__head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.75rem;
  flex-wrap: wrap;
}
.ag__title {
  font-weight: 600;
}
.ag__headline {
  font-size: 0.9rem;
}
.ag__actions {
  display: flex;
  gap: 0.4rem;
}
.ag__block h5 {
  margin: 0 0 0.3rem;
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--color-muted);
}
.ag__decisions {
  margin: 0.25rem 0 0;
  padding-left: 1.1rem;
  font-size: 0.85rem;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}
.ag__signers {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
}
.signer {
  display: grid;
  grid-template-columns: 1rem 1fr auto;
  gap: 0.6rem;
  align-items: start;
  padding: 0.45rem 0;
  border-top: 1px solid var(--color-border);
}
.signer:first-child {
  border-top: none;
}
.signer__dot {
  width: 0.75rem;
  height: 0.75rem;
  margin-top: 0.3rem;
  border-radius: 50%;
  border: 2px solid var(--color-muted);
}
.signer__dot--done {
  background: var(--color-success);
  border-color: var(--color-success);
}
.signer__role {
  font-size: 0.88rem;
  font-weight: 500;
}
.sign-form {
  display: grid;
  grid-template-columns: 1fr 10rem;
  gap: 0.5rem 0.75rem;
  margin-top: 0.5rem;
  padding: 0.6rem 0.7rem;
  border-radius: var(--radius-sm);
  background: var(--color-surface-alt);
  font-size: 0.85rem;
}
.sign-form label {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}
.sign-form__wide {
  grid-column: 1 / -1;
}
.sign-form__actions {
  grid-column: 1 / -1;
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}
.gap-text {
  color: var(--color-error);
}
.ok-text {
  color: var(--color-success);
}
.warn-text {
  color: var(--color-warn-ink);
}
.muted {
  color: var(--color-muted);
}
.small {
  font-size: 0.82rem;
}
button.link {
  background: none;
  border: none;
  color: var(--color-accent);
  cursor: pointer;
  font-size: inherit;
  padding: 0;
}
button.link.muted {
  color: var(--color-muted);
}
a.link {
  color: var(--color-accent);
  text-decoration: none;
}
.placeholder {
  margin-left: 0.35rem;
  padding: 0 0.35rem;
  border-radius: 3px;
  font-size: 0.72rem;
  border: 1px dashed var(--color-muted);
  color: var(--color-muted);
}
</style>
